import type { MerchantApiClient } from "../../types.js";
import type {
  MerchantNotification,
  MerchantAttentionNotification,
  MerchantAttentionNotificationPage,
  NotificationListFilters,
  NotificationListPage,
  NotificationPreferenceRecord,
} from "./models.js";

interface ListEnvelope<T> {
  items: T[];
  nextCursor: string | null;
  unreadCount?: number;
  failedCount?: number;
  meta: { requestId: string };
}

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

export interface NotificationsApi {
  getPreferences(): Promise<NotificationPreferenceRecord>;
  listNotifications(filters: NotificationListFilters): Promise<NotificationListPage>;
  listAttentionNotifications(
    limit: number,
    cursor?: string,
  ): Promise<MerchantAttentionNotificationPage>;
  markAttentionRead(notificationId: string): Promise<MerchantAttentionNotification>;
  markRead(notificationId: string, idempotencyKey: string): Promise<MerchantNotification>;
  retryDelivery(notificationId: string, idempotencyKey: string): Promise<MerchantNotification>;
  updatePreferences(
    record: NotificationPreferenceRecord,
    idempotencyKey: string,
  ): Promise<NotificationPreferenceRecord>;
}

export function createNotificationsApi(client: MerchantApiClient): NotificationsApi {
  return {
    async getPreferences() {
      return unwrap(
        await client.get<DataEnvelope<NotificationPreferenceRecord>>(
          "/v1/api/notification-preferences",
        ),
      );
    },
    async listNotifications(filters) {
      const envelope = await client.get<ListEnvelope<MerchantNotification>>(
        `/v1/api/notifications?${query(filters)}`,
      );
      return {
        notifications: envelope.items,
        nextCursor: envelope.nextCursor,
        unreadCount:
          envelope.unreadCount ?? envelope.items.filter((item) => item.status !== "READ").length,
        failedCount:
          envelope.failedCount ?? envelope.items.filter((item) => item.status === "FAILED").length,
      };
    },
    async listAttentionNotifications(limit, cursor) {
      const queryParams = new URLSearchParams({ limit: String(limit) });
      if (cursor !== undefined) queryParams.set("cursor", cursor);
      const envelope = await client.get<ListEnvelope<MerchantAttentionNotification>>(
        `/v1/api/notifications/attention?${queryParams.toString()}`,
      );
      return {
        items: envelope.items,
        nextCursor: envelope.nextCursor,
        unreadCount: envelope.unreadCount ?? envelope.items.filter((item) => !item.isRead).length,
      };
    },
    async markAttentionRead(notificationId) {
      return unwrap(
        await client.patch<DataEnvelope<MerchantAttentionNotification>>(
          `/v1/api/notifications/attention/${encodeURIComponent(notificationId)}`,
          {},
        ),
      );
    },
    async markRead(notificationId, idempotencyKey) {
      return unwrap(
        await client.patch<DataEnvelope<MerchantNotification>>(
          `/v1/api/notifications/${encodeURIComponent(notificationId)}`,
          {},
          { idempotencyKey },
        ),
      );
    },
    async retryDelivery(notificationId, idempotencyKey) {
      return unwrap(
        await client.post<DataEnvelope<MerchantNotification>>(
          `/v1/api/notifications/${encodeURIComponent(notificationId)}/retry`,
          {},
          { idempotencyKey },
        ),
      );
    },
    async updatePreferences(record, idempotencyKey) {
      return unwrap(
        await client.put<DataEnvelope<NotificationPreferenceRecord>>(
          "/v1/api/notification-preferences",
          { rules: record.rules, recipients: record.recipients },
          { idempotencyKey },
        ),
      );
    },
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function query(filters: NotificationListFilters): string {
  return new URLSearchParams(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
